import {
  type MaintenanceChecklistDefinition,
  type MaintenanceChecklistField,
  type MaintenanceTemplateKind,
} from '@gb-ticket/shared-types';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  type ChecklistFieldPatch,
  updateField,
} from '@/products/maintenance/features/templates/checklist-editor-state';

type Props = {
  definition: MaintenanceChecklistDefinition;
  sectionId: string;
  field: MaintenanceChecklistField;
  kind: MaintenanceTemplateKind;
  pdfPageCount?: number | null;
  onChange: (next: MaintenanceChecklistDefinition) => void;
};

function patch(
  definition: MaintenanceChecklistDefinition,
  sectionId: string,
  fieldKey: string,
  p: ChecklistFieldPatch,
  onChange: (next: MaintenanceChecklistDefinition) => void,
) {
  onChange(updateField(definition, sectionId, fieldKey, p));
}

export function ChecklistFieldProperties({
  definition,
  sectionId,
  field,
  kind,
  pdfPageCount,
  onChange,
}: Props) {
  const apply = (p: ChecklistFieldPatch) =>
    patch(definition, sectionId, field.fieldKey, p, onChange);

  return (
    <div className="mt-2 grid gap-2">
      <div className="space-y-1">
        <Label className="text-xs">Bezeichnung</Label>
        <Input value={field.label} onChange={(e) => apply({ label: e.target.value })} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Hilfetext</Label>
        <Input
          value={field.helpText ?? ''}
          onChange={(e) => apply({ helpText: e.target.value.trim() ? e.target.value : null })}
        />
      </div>
      {field.type !== 'info' ? (
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={field.required}
            onChange={(e) => apply({ required: e.target.checked })}
          />
          Pflichtfeld
        </label>
      ) : null}
      {field.type === 'info' ? (
        <div className="space-y-1">
          <Label className="text-xs">Inhalt</Label>
          <Textarea
            rows={3}
            value={field.content}
            onChange={(e) => apply({ content: e.target.value })}
          />
        </div>
      ) : null}
      {field.type === 'choice' ? (
        <div className="space-y-1">
          <Label className="text-xs">Optionen (Wert|Label pro Zeile)</Label>
          <Textarea
            rows={4}
            value={field.options.map((o) => `${o.value}|${o.label}`).join('\n')}
            onChange={(e) => {
              const options = e.target.value
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean)
                .map((line) => {
                  const [rawValue, ...rest] = line.split('|');
                  const value = (rawValue ?? '').trim();
                  const label = rest.join('|').trim() || value;
                  return { value, label };
                });
              if (options.length > 0) {
                apply({ options });
              }
            }}
          />
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={field.allowMultiple}
              onChange={(e) => apply({ allowMultiple: e.target.checked })}
            />
            Mehrfachauswahl
          </label>
        </div>
      ) : null}
      {field.type === 'short_text' || field.type === 'long_text' ? (
        <div className="space-y-1">
          <Label className="text-xs">Max. Länge</Label>
          <Input
            type="number"
            value={field.maxLength}
            onChange={(e) => apply({ maxLength: Number(e.target.value) })}
          />
        </div>
      ) : null}
      {field.type === 'measurement' ? (
        <div className="space-y-1">
          <Label className="text-xs">Einheit</Label>
          <Input value={field.unit} onChange={(e) => apply({ unit: e.target.value })} />
        </div>
      ) : null}
      {field.type === 'integer' || field.type === 'decimal' || field.type === 'measurement' ? (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Min</Label>
            <Input
              type="number"
              value={field.min ?? ''}
              onChange={(e) =>
                apply({ min: e.target.value === '' ? undefined : Number(e.target.value) })
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max</Label>
            <Input
              type="number"
              value={field.max ?? ''}
              onChange={(e) =>
                apply({ max: e.target.value === '' ? undefined : Number(e.target.value) })
              }
            />
          </div>
        </div>
      ) : null}
      {kind === 'pdf_overlay' && field.placement ? (
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>
            PDF Seite {field.placement.page}
            {pdfPageCount ? ` / ${pdfPageCount}` : ''}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Seite</Label>
              <Input
                type="number"
                min={1}
                value={field.placement.page}
                onChange={(e) =>
                  apply({
                    placement: {
                      ...field.placement!,
                      page: Math.max(1, Number(e.target.value) || 1),
                    },
                  })
                }
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
